import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

import type { TransformedLog, TransformedLogChange } from '../util/types';

type ValueProps = {
  value: { id: string | null; value: string };
  emphasized?: boolean;
};

function ChangeValue({ value, emphasized }: ValueProps) {
  if (!value.value) {
    return <span className="text-muted-foreground italic">empty</span>;
  }
  if (value.id) {
    return (
      <Link
        to={`/audit/${value.id}`}
        className="text-primary hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {value.value}
      </Link>
    );
  }
  return (
    <span
      className={cn(emphasized ? 'text-foreground' : 'text-muted-foreground')}
    >
      {value.value}
    </span>
  );
}

type LabelKind = 'old' | 'new';

function ValueLabel({ kind }: { kind: LabelKind }) {
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center rounded-md px-1.5 font-mono text-[10px] font-semibold tracking-wider uppercase',
        kind === 'old'
          ? 'bg-muted text-muted-foreground'
          : 'bg-primary/10 text-primary',
      )}
    >
      {kind === 'old' ? 'Old' : 'New'}
    </span>
  );
}

type RowProps = {
  change: TransformedLogChange;
  operationType: string;
};

function ChangeRow({ change, operationType }: RowProps) {
  const showOld = operationType === 'UPDATE' || operationType === 'DELETE';
  const showNew = operationType === 'UPDATE' || operationType === 'INSERT';

  return (
    <div className="grid grid-cols-[10rem_auto_1fr] items-baseline gap-x-4 py-1 text-sm">
      <div className="text-foreground font-medium">{change.column}</div>
      <div className="flex min-w-0 items-baseline gap-1.5">
        {showOld ? (
          <>
            <ValueLabel kind="old" />
            <span className="min-w-0 break-words">
              <ChangeValue value={change.originalValue} />
            </span>
          </>
        ) : null}
      </div>
      <div className="flex min-w-0 items-baseline gap-1.5">
        {showNew ? (
          <>
            <ValueLabel kind="new" />
            <span className="min-w-0 break-words">
              <ChangeValue value={change.newValue} emphasized />
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
}

export function ChangesPanel({ data }: { data: TransformedLog }) {
  if (!data.changes.length) {
    return (
      <div className="text-muted-foreground px-4 py-2 text-sm">
        No field changes recorded.
      </div>
    );
  }

  const count = data.changes.length;

  return (
    <div className="bg-muted/30 border-primary/30 border-l-2">
      <div className="border-border/60 text-muted-foreground border-b px-4 py-1 text-xs font-medium">
        {count} {count === 1 ? 'field' : 'fields'} changed
      </div>
      <div className="max-h-[60vh] overflow-y-auto px-4 py-1">
        <div className="divide-border/60 divide-y">
          {data.changes.map((change) => (
            <ChangeRow
              key={change.id}
              change={change}
              operationType={data.operationType}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
